import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { useQueryParams } from 'use-query-params'

import {
    deleteRuleData,
    getRuleData,
    postRuleData,
    putRuleData,
} from '../../../api/rules'
import useApiCall from '../../../api/useApiCall'
import { ReactComponent as Add } from '../../../assets/icons/plus.svg'
import { ReactComponent as Delete } from '../../../assets/icons/trash.svg'
import { ISaveError } from '../../../types/error'
import { buildQuery, DashArrayParam } from '../../../utils/useQueryParams'
import LoadingSection from '../../LoadingSection/LoadingSection'
import MonacoSection from '../../MonacoSection/MonacoSection'
import { IRuleOption } from '../Rules'
import RulesFilters, { RulesFiltersEnum } from '../RulesFilters/RulesFilters'

import { monacoRuleTemplate } from './monacoRuleTemplate'

import styles from './rule-content.module.scss'
export interface IRule {
    name: string
    _id: string
    yml: string
}
interface IRuleContentProps {
    rules: IRuleOption[]
    setRules: React.Dispatch<React.SetStateAction<IRuleOption[]>>
}

function RuleContent(props: IRuleContentProps) {
    const { rules, setRules } = props

    const { t } = useTranslation()

    const { ruleId } = useParams()

    const navigate = useNavigate()

    const [query] = useQueryParams({
        [RulesFiltersEnum.INTENTS]: DashArrayParam,
        [RulesFiltersEnum.ACTIONS]: DashArrayParam,
    })

    const [ruleData, setRuleData] = useState('')
    const [currentRule, setCurrentRule] = useState<IRule>()
    const [isRuleCreating, setIsRuleCreating] = useState(false)
    const [newRuleData, setNewRuleData] = useState(monacoRuleTemplate)

    const [{ loading, data }] = useApiCall(
        getRuleData,
        handleGetRuleData,
        ruleId || ''
    )

    function handleGetRuleData(data: IRule) {
        setRuleData(data.yml)
        setCurrentRule(data)
    }

    const deleteRule = async () => {
        if (!ruleId) return

        if (isRuleCreating) {
            setNewRuleData(monacoRuleTemplate)
            setIsRuleCreating(false)
            return
        }

        setRules((rules) => {
            const filteredRules = rules.filter((rule) => rule._id !== ruleId)
            const [defaultRule] = filteredRules
            navigate(`./../${defaultRule._id}`)
            return filteredRules
        })
        deleteRuleData(ruleId)
    }

    const saveChanges = async (data: string) => {
        if (isRuleCreating) {
            const { _id, name } = await postRuleData(data)
                .then((res) => res.data)
                .catch((error: AxiosError) => {
                    const errorData = error.response?.data as ISaveError

                    toast(`${t(errorData.error)}: ${t(errorData.invalidValue)}`)

                    throw new Error()
                })

            setRules((rules) => [{ _id, name }, ...rules])

            setNewRuleData(monacoRuleTemplate)
            setIsRuleCreating(false)
            navigate(`/rules/${_id}`)
            return
        }

        if (!ruleId || !data) {
            return
        }

        const name = await putRuleData(ruleId, data)
            .then((res) => res.data)
            .catch((error: AxiosError) => {
                const errorData = error.response?.data as ISaveError

                toast(`${t(errorData.error)}: ${t(errorData.invalidValue)}`, {
                    type: 'error',
                })

                throw new Error()
            })

        if (!name) return

        setRules((rules) => {
            const clonedRules = structuredClone(rules)
            const ruleToChange = clonedRules.find((rule) => rule._id === ruleId)
            if (ruleToChange) {
                ruleToChange.name = name
            }

            return clonedRules
        })
    }

    const handleChangeRuleId = (ruleId: string) => {
        navigate({ pathname: `/rules/${ruleId}`, search: buildQuery(query) })
    }

    if (loading) {
        return <LoadingSection />
    }

    const getMonacoValue = () => {
        return isRuleCreating ? newRuleData : ruleData
    }

    return (
        <MonacoSection
            value={getMonacoValue()}
            handleSave={saveChanges}
            navigation={rules}
            currentNavigationOption={
                isRuleCreating ? { _id: '', name: '' } : currentRule
            }
            rawData={data?.yml || ''}
            handleSelect={handleChangeRuleId}
            hideSaved={!rules.length}
            error={!isRuleCreating && !rules.length ? t('no_rules') : undefined}
        >
            <>
                {rules.length && (
                    <button
                        className={styles.ruleContent__btn}
                        onClick={deleteRule}
                    >
                        <Delete className={styles.ruleContent__icon} />
                    </button>
                )}
                <button
                    className={styles.ruleContent__btn}
                    onClick={() => setIsRuleCreating(true)}
                >
                    <Add className={styles.ruleContent__icon} />
                </button>
            </>
            <RulesFilters />
        </MonacoSection>
    )
}

export default RuleContent
